// apps/client/src/services/recordCollection.ts

import {
  RecordCollection,
  NewRecordCollection,
  UpdateRecordCollection,
  RecordCollectionListResponse,
  RecordCollectionCreateResponse,
  RecordCollectionGetResponse,
  RecordCollectionUpdateResponse,
  RecordCollectionAttachment,
  NewRecordCollectionAttachment,
  RecordCollectionAttachmentCreateResponse,
} from '@fsp/types';
import { apiClient } from './apiClient';

export const fetchRecordCollections = async (
  organisationId: string,
  planId: string,
  signal?: AbortSignal
): Promise<RecordCollection[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/records`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch record collections: ${response.statusText}`);
  }

  const result: RecordCollectionListResponse = await response.json();
  return result.collections || [];
};

export const fetchRecordCollection = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  signal?: AbortSignal
): Promise<{ collection: RecordCollection; attachments: RecordCollectionAttachment[] }> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch record collection: ${response.statusText}`);
  }

  const result: RecordCollectionGetResponse = await response.json();
  return {
    collection: result.collection,
    attachments: result.attachments || [],
  };
};

export const fetchRecordCollectionByIdOnly = async (
  collectionId: string,
  signal?: AbortSignal
): Promise<{ collection: RecordCollection; attachments: RecordCollectionAttachment[]; organisationId: string }> => {
  const response = await apiClient.get(
    `/collections/${collectionId}`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch record collection: ${response.statusText}`);
  }

  const result: RecordCollectionGetResponse = await response.json();
  return {
    collection: result.collection,
    attachments: result.attachments || [],
    organisationId: result.organisationId || '',
  };
};

export const createRecordCollection = async (
  organisationId: string,
  planId: string,
  data: NewRecordCollection,
  signal?: AbortSignal
): Promise<RecordCollection> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/records`,
    data,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create record collection: ${response.statusText}`);
  }

  const result: RecordCollectionCreateResponse = await response.json();
  return result.collection;
};

export const updateRecordCollection = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  data: UpdateRecordCollection,
  signal?: AbortSignal
): Promise<RecordCollection> => {
  const response = await apiClient.put(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}`,
    data,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to update record collection: ${response.statusText}`);
  }

  const result: RecordCollectionUpdateResponse = await response.json();
  return result.collection;
};

export const deleteRecordCollection = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  signal?: AbortSignal
): Promise<void> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}`,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to delete record collection: ${response.statusText}`);
  }
};

export const createRecordCollectionAttachment = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  data: NewRecordCollectionAttachment,
  signal?: AbortSignal
): Promise<RecordCollectionAttachment> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}/attachments`,
    data,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create attachment: ${response.statusText}`);
  }

  const result: RecordCollectionAttachmentCreateResponse = await response.json();
  return result.attachment;
};

export const deleteRecordCollectionAttachment = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  attachmentId: string,
  signal?: AbortSignal
): Promise<void> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}/attachments/${attachmentId}`,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to delete attachment: ${response.statusText}`);
  }
};

export const fetchRecordCollectionAttachmentsByTarget = async (
  organisationId: string,
  planId: string,
  attachmentType: string,
  attachmentId: string,
  signal?: AbortSignal
): Promise<RecordCollectionAttachment[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/records/attachments?attachmentType=${attachmentType}&attachmentId=${attachmentId}`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch attachments: ${response.statusText}`);
  }

  const result = await response.json();
  return result.attachments || [];
};
